/**
 * Levels 7–12 (Grades 4–6): the world opens up — reefs, canyons, floating
 * islands, a balloon festival, a sunlit crystal grotto and a desert oasis.
 */
import type { ThemePack } from '../types.ts';
import { CLOUD_WHITE, FESTIVE } from './shared.ts';

const coralLagoon: ThemePack = {
  id: 'coral-lagoon',
  level: 7,
  name: { en: 'Coral Lagoon', af: 'Koraal-strandmeer' },
  tagline: { en: 'Crystal water over a rainbow reef.', af: "Kristalhelder water oor 'n reënboogrif." },
  backgroundConcept:
    'A calm, glassy lagoon so clear you can see the reef: pink branch coral, orange fans, purple brain coral and little striped fish, with a palm-topped island on the horizon and a white sandbar in front.',
  uiNotes: 'White panels with deep-teal text, teal buttons and a flamingo-pink accent. Coral bricks.',
  animationIdeas: ['Fish dart between the corals', 'Bubbles rise from the reef', 'Ripples and sparkles on the surface'],
  soundNotes: 'B major pentatonic steel pan at 90 bpm, glassy pad, soft underwater bubbles.',
  palette: {
    skyTop: '#45c6f2',
    skyBottom: '#c8fbf4',
    wellBg: '#ffffff59',
    wellBorder: '#ffffffcc',
    blockBg: '#fffbf4',
    blockBorder: '#ff7f6e',
    blockText: '#0f2f45',
    brickA: '#ff9f8a',
    brickB: '#ff8672',
    brickMortar: '#fff0e8',
    uiPanel: '#ffffffe6',
    uiText: '#0f2f45',
    uiMuted: '#4f6d80',
    accent: '#ff6f91',
    buttonBg: '#0a7c8c',
    buttonText: '#ffffff',
    good: '#1fa971',
    bad: '#ef5f67',
  },
  brickStyle: 'coral',
  particles: { kind: 'bubbles', density: 0.5, color: '#e6fbff' },
  scene: [
    { kind: 'sky', mid: '#a6ecff', midAt: 0.5 },
    { kind: 'sun', x: 0.8, y: 0.12, radius: 0.065, color: '#fff176', glow: '#fff7c4', rays: 10, rayColor: '#fffbe0' },
    { kind: 'clouds', count: 4, yMin: 0.05, yMax: 0.28, size: 0.22, color: CLOUD_WHITE, shade: '#cff3f5', speed: 0.7, depth: 0.05 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.5, height: 0.07, color: '#7fd6a3', roughness: 0.4, mist: 0.3, depth: 0.1 },
    { kind: 'trees', species: 'palm', count: 3, baseline: 0.5, size: 0.09, spread: [0.12, 0.3], color: '#5dc46a', trunk: '#b5895a', depth: 0.1 },
    { kind: 'water', top: 0.5, color: '#5fe0e0', deep: '#1fb3c9', foam: '#ffffff', waves: 0.35, sparkle: true, depth: 0.2 },
    {
      kind: 'reef',
      top: 0.62,
      bottom: 0.95,
      count: 14,
      colors: ['#ff8fa3', '#ffb86b', '#b197fc', '#ff6b9a', '#ffd43b'],
      wash: '#35c9d6',
      fish: 6,
      depth: 0.2,
    },
    { kind: 'ground', top: 0.9, color: '#ffe8b5', texture: 'sand', accent: '#f5cf8a', depth: 0.7 },
  ],
  audio: { key: 'B', scale: 'majorPentatonic', bpm: 90, lead: 'steelpan', pad: 'glass', ambience: 'bubbles' },
};

const canyonRapids: ThemePack = {
  id: 'canyon-rapids',
  level: 8,
  name: { en: 'Canyon Rapids', af: 'Wildewaterkloof' },
  tagline: { en: 'Ride the rapids between red rock walls.', af: 'Ry die stroomversnellings tussen rooi rotsmure.' },
  backgroundConcept:
    'Sun-baked orange canyon walls with striped rock layers, flat-topped mesas fading into the distance, a waterfall spilling off a ledge and a bright turquoise river full of white rapids. Hawks circle overhead.',
  uiNotes: 'Warm sand panels, dark umber text, burnt-orange buttons and a tangerine accent. Sandstone bricks.',
  animationIdeas: ['White water churns in the rapids', 'A side waterfall streams down', 'Spray drifts up from the river', 'Hawks glide in slow arcs'],
  soundNotes: 'E mixolydian plucks at a lively 104 bpm, no pad (open and twangy), a dry canyon wind.',
  palette: {
    skyTop: '#4cb8f2',
    skyBottom: '#ffe2c4',
    wellBg: '#fffaf35c',
    wellBorder: '#fff1e0cc',
    blockBg: '#fffaf3',
    blockBorder: '#ff8c42',
    blockText: '#2b1a10',
    brickA: '#e8a86f',
    brickB: '#d9925a',
    brickMortar: '#fff0db',
    uiPanel: '#fffaf4e8',
    uiText: '#2b1a10',
    uiMuted: '#6e5646',
    accent: '#ff7a3d',
    buttonBg: '#c2410c',
    buttonText: '#ffffff',
    good: '#2a9d5c',
    bad: '#e05a5a',
  },
  brickStyle: 'sandstone',
  particles: { kind: 'spray', density: 0.4, color: '#ffffff' },
  scene: [
    { kind: 'sky', mid: '#bfe8ff', midAt: 0.45 },
    { kind: 'sun', x: 0.8, y: 0.12, radius: 0.06, color: '#ffe066', glow: '#fff1b8' },
    { kind: 'clouds', count: 3, yMin: 0.06, yMax: 0.25, size: 0.24, color: CLOUD_WHITE, shade: '#ffe1cf', speed: 0.6, style: 'long', depth: 0.05 },
    { kind: 'birds', style: 'gull', count: 3, yMin: 0.14, yMax: 0.3, size: 0.05, colors: ['#8a5a44'], speed: 2.5, depth: 0.1 },
    { kind: 'ridges', shape: 'mesa', baseline: 0.56, height: 0.2, color: '#f2a477', stripes: '#e58e62', roughness: 0.4, mist: 0.35, depth: 0.1 },
    { kind: 'cliffs', side: 'left', width: 0.28, top: 0.25, color: '#e8845a', stripes: '#d56f47', moss: '#8fcf6a', depth: 0.45 },
    { kind: 'waterfall', x: 0.2, top: 0.44, bottom: 0.76, width: 0.05, color: '#8fe3ff', foam: '#ffffff', rock: '#d56f47', depth: 0.45 },
    { kind: 'cliffs', side: 'right', width: 0.26, top: 0.3, color: '#e8845a', stripes: '#d56f47', moss: '#8fcf6a', depth: 0.45 },
    { kind: 'water', top: 0.74, color: '#4fc3e0', deep: '#2a9fc8', foam: '#ffffff', waves: 1, sparkle: true, depth: 0.5 },
    { kind: 'ground', top: 0.92, color: '#d98a5f', texture: 'rock', accent: '#b86f4a', depth: 0.8 },
  ],
  audio: { key: 'E', scale: 'mixolydian', bpm: 104, lead: 'pluck', pad: 'none', ambience: 'wind' },
};

const skyIslands: ThemePack = {
  id: 'sky-islands',
  level: 9,
  name: { en: 'Sky Islands', af: 'Hemeleilande' },
  tagline: { en: 'Hop across islands floating in the clouds.', af: 'Spring van eiland tot eiland hoog in die wolke.' },
  backgroundConcept:
    'No ground at all: grassy islands with rocky roots float above a sea of clouds, little waterfalls pouring off their edges into the mist. A double rainbow arcs behind them and birds wheel between.',
  uiNotes: 'White panels with indigo text, violet buttons and accent. Pale marble bricks.',
  animationIdeas: ['Islands bob up and down very slowly', 'Streams pour off the island rims', 'The cloud sea drifts below', 'Soft feathers float past'],
  soundNotes: 'F lydian flute at 86 bpm, airy pad, high soft wind.',
  palette: {
    skyTop: '#6ec6ff',
    skyBottom: '#fde8f5',
    wellBg: '#ffffff5c',
    wellBorder: '#ffffffcc',
    blockBg: '#fdfcff',
    blockBorder: '#8c7bff',
    blockText: '#1e2350',
    brickA: '#e9e4f5',
    brickB: '#d8d0ec',
    brickMortar: '#ffffff',
    uiPanel: '#ffffffe6',
    uiText: '#1e2350',
    uiMuted: '#5e6390',
    accent: '#8c7bff',
    buttonBg: '#5b4bd6',
    buttonText: '#ffffff',
    good: '#27a567',
    bad: '#e8656a',
  },
  brickStyle: 'marble',
  particles: { kind: 'feathers', density: 0.3, color: '#ffffff' },
  scene: [
    { kind: 'sky', mid: '#dff0ff', midAt: 0.5 },
    { kind: 'sun', x: 0.15, y: 0.12, radius: 0.07, color: '#fff07a', glow: '#fff8cc', rays: 12, rayColor: '#fffbe3' },
    { kind: 'rainbow', x: 0.62, y: 0.78, radius: 0.46, width: 0.08, opacity: 0.4, double: true },
    { kind: 'clouds', count: 6, yMin: 0.72, yMax: 0.95, size: 0.36, color: CLOUD_WHITE, shade: '#e3e8ff', speed: 0.5, depth: 0.1 },
    {
      kind: 'islands',
      count: 3,
      yMin: 0.24,
      yMax: 0.44,
      size: 0.24,
      grass: '#7fd66b',
      rock: '#c9a98a',
      trees: '#5dbb5a',
      waterfall: '#9be7ff',
      spread: [0.05, 0.95],
      depth: 0.3,
    },
    { kind: 'birds', style: 'gull', count: 4, yMin: 0.15, yMax: 0.4, size: 0.045, colors: ['#ffffff'], speed: 3, depth: 0.35 },
    {
      kind: 'islands',
      count: 2,
      yMin: 0.55,
      yMax: 0.68,
      size: 0.36,
      grass: '#6cce5a',
      rock: '#b8977a',
      trees: '#4fae4f',
      waterfall: '#9be7ff',
      spread: [0, 1],
      depth: 0.6,
      seed: 7,
    },
    { kind: 'clouds', count: 3, yMin: 0.86, yMax: 1, size: 0.45, color: CLOUD_WHITE, shade: '#e6e9ff', speed: 0.8, depth: 0.8, seed: 11 },
  ],
  audio: { key: 'F', scale: 'lydian', bpm: 86, lead: 'flute', pad: 'airy', ambience: 'wind' },
};

const balloonFestival: ThemePack = {
  id: 'balloon-festival',
  level: 10,
  name: { en: 'Balloon Festival', af: 'Ballonfees' },
  tagline: { en: 'Up, up and away over the festival fields!', af: 'Op, op en weg oor die feesvelde!' },
  backgroundConcept:
    'A summer fair on rolling green hills: striped hot-air balloons rising at every distance, candy-striped marquee tents, strings of bunting across the top and confetti in the air.',
  uiNotes: 'White panels, grape text, raspberry buttons and accent. Painted wooden-crate bricks.',
  animationIdeas: ['Balloons bob and drift at different speeds (parallax)', 'Bunting flutters', 'Confetti tumbles down', 'Clouds drift'],
  soundNotes: 'G major pentatonic marimba at a cheerful 108 bpm, warm pad, birdsong and a distant happy crowd feel.',
  palette: {
    skyTop: '#5ac8fa',
    skyBottom: '#fff0c9',
    wellBg: '#ffffff59',
    wellBorder: '#ffffffcc',
    blockBg: '#fffdf5',
    blockBorder: '#ff5d8f',
    blockText: '#26203a',
    brickA: '#d99a5f',
    brickB: '#c9854b',
    brickMortar: '#fbe6cc',
    uiPanel: '#ffffffe6',
    uiText: '#26203a',
    uiMuted: '#655d7a',
    accent: '#ff5d8f',
    buttonBg: '#c2255c',
    buttonText: '#ffffff',
    good: '#2aa65f',
    bad: '#e8594f',
  },
  brickStyle: 'wood',
  particles: { kind: 'confetti', density: 0.4, color: '#ffd43b' },
  scene: [
    { kind: 'sky', mid: '#bfeaff', midAt: 0.6 },
    { kind: 'sun', x: 0.85, y: 0.1, radius: 0.06, color: '#ffe45c', glow: '#fff3b0', rays: 12, rayColor: '#fff7cf' },
    { kind: 'clouds', count: 5, yMin: 0.05, yMax: 0.3, size: 0.2, color: CLOUD_WHITE, shade: '#d8ecff', speed: 0.7, depth: 0.05 },
    { kind: 'balloons', count: 5, yMin: 0.14, yMax: 0.38, size: 0.06, colors: FESTIVE, speed: 1.2, depth: 0.1 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.66, height: 0.1, color: '#9fdc8a', roughness: 0.3, mist: 0.3, depth: 0.15 },
    { kind: 'ridges', shape: 'terraces', baseline: 0.78, height: 0.1, color: '#86d26f', stripes: '#a6e38e', roughness: 0.3, depth: 0.4 },
    { kind: 'structure', type: 'tents', x: 0.22, baseline: 0.8, size: 0.12, color: '#ffffff', accent: '#ff6b6b', trim: '#ffd43b', depth: 0.4 },
    { kind: 'balloons', count: 3, yMin: 0.3, yMax: 0.55, size: 0.13, colors: FESTIVE, speed: 1.8, depth: 0.55, seed: 5 },
    { kind: 'ground', top: 0.88, color: '#77cc62', texture: 'grass', accent: '#5aae4c', depth: 0.75 },
    { kind: 'bunting', y: 0.03, sag: 0.05, rows: 2, colors: ['#ff6b6b', '#ffd43b', '#4dabf7', '#69db7c', '#f783ac'], string: '#ffffff', depth: 0.9 },
  ],
  audio: { key: 'G', scale: 'majorPentatonic', bpm: 108, lead: 'marimba', pad: 'warm', ambience: 'birds' },
};

const crystalGrotto: ThemePack = {
  id: 'crystal-grotto',
  level: 11,
  name: { en: 'Crystal Grotto', af: 'Kristalgrot' },
  tagline: { en: 'Sunbeams dance on sparkling crystals.', af: 'Sonstrale dans op glinsterende kristalle.' },
  backgroundConcept:
    'An open-roofed grotto flooded with sunshine — never a dark cave. Lilac rock walls frame a bright sky, sunbeams pour in from above onto clusters of pastel crystals and a turquoise pool.',
  uiNotes: 'Near-white lilac panels, deep violet text and buttons, a pink accent. Crystal bricks in lilac and ice blue.',
  animationIdeas: ['Glints twinkle on crystal tips', 'Sunbeams shimmer', 'Sparkles drift in the light', 'The pool ripples softly'],
  soundNotes: 'E lydian bells at a slow 78 bpm with a glass pad; a soft trickling-stream bed.',
  palette: {
    skyTop: '#8ad4ff',
    skyBottom: '#f3dcff',
    wellBg: '#ffffff5c',
    wellBorder: '#fbf5ffcc',
    blockBg: '#fdfbff',
    blockBorder: '#b197fc',
    blockText: '#23184a',
    brickA: '#c9b6ff',
    brickB: '#a9e4ff',
    brickMortar: '#f7f3ff',
    uiPanel: '#fffbffe8',
    uiText: '#23184a',
    uiMuted: '#665c8f',
    accent: '#e64980',
    buttonBg: '#6741d9',
    buttonText: '#ffffff',
    good: '#2ca36b',
    bad: '#e5607a',
  },
  brickStyle: 'crystal',
  particles: { kind: 'sparkles', density: 0.5, color: '#ffffff' },
  scene: [
    { kind: 'sky', mid: '#d6ecff', midAt: 0.5 },
    { kind: 'lightRays', x: 0.5, count: 7, spread: 1, color: '#fffbe6', opacity: 0.45 },
    { kind: 'clouds', count: 3, yMin: 0.04, yMax: 0.2, size: 0.2, color: CLOUD_WHITE, shade: '#eadcff', speed: 0.4, depth: 0.05 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.62, height: 0.2, color: '#d9c8f5', roughness: 0.5, mist: 0.3, depth: 0.1 },
    { kind: 'crystals', count: 4, baseline: 0.63, size: 0.12, colors: ['#c5b3ff', '#a5e8ff', '#ffc2e2'], spread: [0.1, 0.9], glint: true, depth: 0.1 },
    { kind: 'cliffs', side: 'left', width: 0.24, top: 0, color: '#c9b3ec', stripes: '#b79fe0', moss: '#8ee0b0', depth: 0.45 },
    { kind: 'cliffs', side: 'right', width: 0.24, top: 0.05, color: '#c9b3ec', stripes: '#b79fe0', moss: '#8ee0b0', depth: 0.45 },
    { kind: 'water', top: 0.8, color: '#7fe6f0', deep: '#46c6e0', foam: '#ffffff', waves: 0.2, sparkle: true, depth: 0.5 },
    {
      kind: 'crystals',
      count: 5,
      baseline: 0.86,
      size: 0.22,
      colors: ['#b197fc', '#74c0fc', '#f783ac', '#63e6be'],
      glint: true,
      depth: 0.7,
      seed: 3,
    },
    { kind: 'ground', top: 0.92, color: '#c3aee6', texture: 'rock', accent: '#a992d6', depth: 0.8 },
  ],
  audio: { key: 'E', scale: 'lydian', bpm: 78, lead: 'bell', pad: 'glass', ambience: 'stream' },
};

const desertOasis: ThemePack = {
  id: 'desert-oasis',
  level: 12,
  name: { en: 'Desert Oasis', af: 'Woestyn-oase' },
  tagline: { en: 'Palm shade and cool water among golden dunes.', af: 'Palmskaduwee en koel water tussen goue duine.' },
  backgroundConcept:
    'Golden dunes under a blazing-but-friendly sun, an ancient stepped sandstone temple on the horizon, and a cool turquoise oasis pool fringed with swaying date palms.',
  uiNotes: 'Warm ivory panels, dark coffee text, teal buttons for contrast with the sand, orange accent. Sandstone bricks.',
  animationIdeas: ['Palms sway over the pool', 'Golden dust glints drift', 'Long thin clouds creep across', 'The pool sparkles'],
  soundNotes: 'A dorian plucked strings at 92 bpm, warm pad, soft desert wind.',
  palette: {
    skyTop: '#4fc0f5',
    skyBottom: '#fff1cf',
    wellBg: '#fffbf25c',
    wellBorder: '#fff6e0cc',
    blockBg: '#fffbf2',
    blockBorder: '#f4b942',
    blockText: '#33210c',
    brickA: '#f0c27b',
    brickB: '#e0ab62',
    brickMortar: '#fff3dc',
    uiPanel: '#fffaf0eb',
    uiText: '#33210c',
    uiMuted: '#72603f',
    accent: '#f08c00',
    buttonBg: '#0f766e',
    buttonText: '#ffffff',
    good: '#2a9d5c',
    bad: '#e0605a',
  },
  brickStyle: 'sandstone',
  particles: { kind: 'pollen', density: 0.3, color: '#ffe8a3' },
  scene: [
    { kind: 'sky', mid: '#bfeaff', midAt: 0.45 },
    { kind: 'sun', x: 0.78, y: 0.13, radius: 0.085, color: '#ffe14d', glow: '#fff0a8', rays: 14, rayColor: '#fff5cc' },
    { kind: 'clouds', count: 2, yMin: 0.08, yMax: 0.2, size: 0.24, color: CLOUD_WHITE, shade: '#ffeccc', speed: 0.4, style: 'long', depth: 0.05 },
    { kind: 'ridges', shape: 'dunes', baseline: 0.62, height: 0.12, color: '#f7d28c', roughness: 0.4, mist: 0.35, depth: 0.1 },
    {
      kind: 'structure',
      type: 'temple',
      x: 0.2,
      baseline: 0.63,
      size: 0.15,
      color: '#e9c083',
      accent: '#f59f00',
      trim: '#c98f4a',
      mound: '#f3cf8a',
      depth: 0.1,
    },
    { kind: 'ridges', shape: 'dunes', baseline: 0.75, height: 0.1, color: '#f3c270', roughness: 0.5, depth: 0.4 },
    { kind: 'water', top: 0.79, color: '#4fd2d9', deep: '#29b3c4', foam: '#ffffff', waves: 0.15, sparkle: true, depth: 0.45 },
    { kind: 'ground', top: 0.88, color: '#f6cf85', texture: 'sand', accent: '#e2b464', depth: 0.75 },
    {
      kind: 'trees',
      species: 'palm',
      count: 5,
      baseline: 0.9,
      size: 0.28,
      color: '#5cbf5a',
      trunk: '#b5875a',
      accent: '#8a5a2b',
      sway: true,
      depth: 0.75,
    },
  ],
  audio: { key: 'A', scale: 'dorian', bpm: 92, lead: 'pluck', pad: 'warm', ambience: 'wind' },
};

export const LEVELS_07_12: ThemePack[] = [coralLagoon, canyonRapids, skyIslands, balloonFestival, crystalGrotto, desertOasis];
